/**
 * ProgressBars — 3-барный виджет «до следующего статуса / ранга»
 * для вкладки «Дерево» (Network страница).
 *
 * 2 карточки:
 *   • СТАТУС (2 бара): Личный депозит + Оборот сети → след. статус
 *   • РАНГ (1 бар): оборот L1 за 30 дней → след. ранг + boost
 *
 * Визуал повторяет `GlassSurface variant="strong"` pattern как у
 * HERO-карточки на Network.tsx — с декоративным цветным блобом,
 * mono-kicker + font-display заголовками, LiquidNumber-ом и
 * внутренними «sub-cards» на var(--glass-fill).
 *
 * Данные — `useLunaProgress()` (SSE-подписка). Полоски реактивно
 * тянутся после депозита без reload. Состояние свёртки — в
 * localStorage.
 */
import { useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown, Trophy, Zap, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GlassSurface, GlassPill } from '@/components/glass';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';
import type {
  ProgressSnapshot,
  ProgressBarData,
  StatusProgress,
  RankProgress,
  LinesProgress,
} from '@/services/api';

const LS_KEY = 'luna.networkTree.progressExpanded.v1';

export function loadProgressExpanded(): boolean {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw === null) return true; // default: развёрнуто
    return raw === '1';
  } catch {
    return true;
  }
}

export function saveProgressExpanded(expanded: boolean): void {
  try {
    localStorage.setItem(LS_KEY, expanded ? '1' : '0');
  } catch {
    /* ignore quota */
  }
}

// ────────────────────────────────────────────────────────────────────
// Formatting helpers (СНГ формат: пробелы для тысяч, макс 3 знака)
// ────────────────────────────────────────────────────────────────────

function formatUsdt(raw: string | number): string {
  const n = typeof raw === 'string' ? parseFloat(raw) : raw;
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const opts: Intl.NumberFormatOptions = abs >= 1000
    ? { maximumFractionDigits: 0 }
    : { maximumFractionDigits: 2 };
  return new Intl.NumberFormat('ru-RU', opts).format(n);
}

function formatCompactUsdt(raw: string | number): string {
  const n = typeof raw === 'string' ? parseFloat(raw) : raw;
  if (!Number.isFinite(n)) return '—';
  if (n >= 1_000_000) {
    return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (n >= 10_000) {
    return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  }
  return formatUsdt(n);
}

// ────────────────────────────────────────────────────────────────────
// Bar row — лейбл сверху, градиентная полоска снизу
// ────────────────────────────────────────────────────────────────────

interface BarRowProps {
  label: string;
  bar: ProgressBarData;
  /** CSS-переменная или color() для акцента полоски */
  accent: string;
}

function BarRow({ label, bar, accent }: BarRowProps) {
  const pct = Math.max(0, Math.min(100, bar.progressPct));
  const isComplete = pct >= 100;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] uppercase tracking-[0.14em]
                         text-[var(--text-secondary)] font-mono">
          {label}
        </span>
        <span
          className="text-[13px] font-display font-extrabold tabular-nums tracking-tight"
          style={{
            color: isComplete ? accent : 'var(--text-primary)',
          }}
        >
          {formatCompactUsdt(bar.current)}
          <span className="text-[var(--text-secondary)] mx-1 font-normal">/</span>
          <span style={{ color: 'var(--text-secondary)' }}>
            {formatCompactUsdt(bar.required)}
          </span>
        </span>
      </div>
      <div
        className="relative h-2 rounded-full overflow-hidden"
        style={{
          background:
            'color-mix(in oklab, var(--glass-stroke) 55%, transparent)',
          boxShadow: 'inset 0 1px 1px rgba(0,0,0,0.35)',
        }}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            width: `${pct}%`,
            background: isComplete
              ? `linear-gradient(90deg,
                  color-mix(in oklab, ${accent} 80%, transparent) 0%,
                  ${accent} 100%)`
              : `linear-gradient(90deg,
                  color-mix(in oklab, ${accent} 45%, transparent) 0%,
                  color-mix(in oklab, ${accent} 85%, transparent) 100%)`,
            boxShadow: isComplete
              ? `0 0 10px color-mix(in oklab, ${accent} 80%, transparent),
                 inset 0 1px 0 rgba(255,255,255,0.25)`
              : `0 0 6px color-mix(in oklab, ${accent} 45%, transparent)`,
            transition: 'width 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Sub-card (Status / Rank) — glass-fill внутренняя карточка
// ────────────────────────────────────────────────────────────────────

interface SubCardProps {
  kicker: string;
  icon: React.ReactNode;
  accent: string;
  currentLabel: string;
  nextLabel: string | null;
  isMaxed: boolean;
  children?: React.ReactNode;
  /** Произвольный блок под баром. Рендерится AS-IS (JSX), чтобы
   *  можно было ставить несколько строк / разные стили. */
  footnote?: React.ReactNode;
}

/**
 * FootnoteBlock — 3 строки под баром:
 *   1) серый explainer (мелкий) — что такое эта метрика
 *   2) чёрный-белый "нужно ещё X" (крупнее)
 *   3) акцентная награда (цветная, мелкая)
 * Каждая строка опциональна — null/undefined игнорится.
 */
function FootnoteBlock({
  accent,
  lines,
}: {
  accent: string;
  lines: Array<string | null | undefined>;
}) {
  const [explainer, need, reward] = lines;
  return (
    <div className="relative mt-3 flex flex-col gap-1.5 font-mono leading-[1.45]">
      {explainer && (
        <p className="text-[12.5px] text-[var(--text-muted)] leading-[1.5]">
          {explainer}
        </p>
      )}
      {need && (
        <p className="text-[12px] text-[var(--text-primary)] font-semibold">
          {need}
        </p>
      )}
      {reward && (
        <p
          className="text-[11.5px]"
          style={{ color: accent, opacity: 0.92 }}
        >
          {reward}
        </p>
      )}
    </div>
  );
}

function SubCard({
  kicker, icon, accent,
  currentLabel, nextLabel, isMaxed,
  children, footnote,
}: SubCardProps) {
  return (
    <div
      className="relative rounded-2xl p-3.5"
      style={{
        background: 'var(--glass-fill)',
        border: '1px solid var(--glass-stroke)',
        boxShadow:
          'inset 0 1px 0 rgba(255,255,255,0.04), 0 1px 2px rgba(0,0,0,0.25)',
      }}
    >
      {/* Тонкий цветной нимб сверху карточки — повторяет язык HERO */}
      <div
        aria-hidden
        className="absolute pointer-events-none rounded-2xl"
        style={{
          inset: 0,
          background: `linear-gradient(180deg,
            color-mix(in oklab, ${accent} 9%, transparent) 0%,
            transparent 55%)`,
        }}
      />

      <div className="relative flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className="inline-flex h-5 w-5 flex-shrink-0 rounded-lg items-center justify-center"
            style={{
              background: `color-mix(in oklab, ${accent} 18%, transparent)`,
              border: `1px solid color-mix(in oklab, ${accent} 40%, transparent)`,
              color: accent,
            }}
          >
            {icon}
          </span>
          <span className="text-[11px] uppercase tracking-[0.18em]
                           text-[var(--text-secondary)] font-mono">
            {kicker}
          </span>
        </div>
        {isMaxed && (
          <GlassPill tone="plasma" compact className="!text-[9px]">
            MAX
          </GlassPill>
        )}
      </div>

      <div className="relative mb-3">
        <p className="font-display text-[16px] font-extrabold
                      text-[var(--text-primary)] tracking-tight leading-tight">
          {currentLabel}
          {nextLabel !== null && (
            <>
              <span className="mx-1.5 text-[var(--text-secondary)] font-medium">→</span>
              <span style={{ color: accent }}>{nextLabel}</span>
            </>
          )}
        </p>
      </div>

      {children && (
        <div className="relative flex flex-col gap-2.5">
          {children}
        </div>
      )}

      {footnote}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Status / Rank renderers
// ────────────────────────────────────────────────────────────────────

function StatusContent({
  status,
  t,
}: {
  status: StatusProgress;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const accent = 'var(--accent-secondary)'; // plasma / purple-ish
  const currentLabel = status.currentLevel > 0 && status.currentName
    ? `${status.currentName} · S${status.currentLevel}`
    : t('progressBars.noStatusYet');

  const explainer = status.isMaxed ? null : t('progressBars.statusExplainer');

  const needLine = (() => {
    if (status.isMaxed) return t('progressBars.maxStatusHint');
    if (!status.personal || !status.network) return null;

    const personalRem = parseFloat(status.personal.remaining);
    const networkRem = parseFloat(status.network.remaining);

    if (personalRem > 0 && networkRem > 0) {
      return t('progressBars.statusRemainingBoth', {
        personal: formatCompactUsdt(personalRem),
        network: formatCompactUsdt(networkRem),
      });
    }
    if (personalRem > 0) {
      return t('progressBars.statusRemainingPersonal', {
        personal: formatCompactUsdt(personalRem),
      });
    }
    if (networkRem > 0) {
      return t('progressBars.statusRemainingNetwork', {
        network: formatCompactUsdt(networkRem),
      });
    }
    return t('progressBars.statusAllReady');
  })();

  const rewardLine = !status.isMaxed && status.nextBonusAmount
    ? t('progressBars.statusReward', {
        amount: formatUsdt(status.nextBonusAmount),
      })
    : null;

  const footnote = <FootnoteBlock accent={accent} lines={[explainer, needLine, rewardLine]} />;

  return (
    <SubCard
      kicker={t('progressBars.statusTitle')}
      icon={<Trophy size={11} strokeWidth={2.2} />}
      accent={accent}
      currentLabel={currentLabel}
      nextLabel={status.isMaxed ? null : status.nextName}
      isMaxed={status.isMaxed}
      footnote={footnote}
    >
      {status.personal && (
        <BarRow
          label={t('progressBars.personalLabel')}
          bar={status.personal}
          accent={accent}
        />
      )}
      {status.network && (
        <BarRow
          label={t('progressBars.networkLabel')}
          bar={status.network}
          accent={accent}
        />
      )}
      {status.personal && status.network && !status.isMaxed && (
        <p
          className="text-[10.5px] leading-[1.45] font-mono mt-1 px-2 py-1.5 rounded-lg"
          style={{
            color: 'var(--text-muted)',
            background:
              'color-mix(in oklab, var(--status-warning) 8%, transparent)',
            border:
              '1px solid color-mix(in oklab, var(--status-warning) 22%, transparent)',
          }}
        >
          {t('progressBars.personalVsNetworkNote')}
        </p>
      )}
    </SubCard>
  );
}

// ────────────────────────────────────────────────────────────────────
// Lines progress — активные прямые рефы → открытие следующей линии.
// ────────────────────────────────────────────────────────────────────

function LinesContent({
  lines,
  t,
}: {
  lines: LinesProgress;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const accent = 'var(--accent-tertiary)'; // cyan / nebula
  const currentLabel = t('progressBars.linesCurrent', {
    line: lines.currentOpenLine,
  });

  const nextLabel = lines.isMaxed
    ? null
    : t('progressBars.linesNext', { line: lines.nextLine });

  const pct = Math.max(0, Math.min(100, lines.progressPct));
  const isComplete = pct >= 100;

  const needLine = (() => {
    if (lines.isMaxed) {
      return t('progressBars.maxLinesHint');
    }
    if (lines.remaining <= 0) {
      return t('progressBars.linesReady', { line: lines.nextLine });
    }
    return t('progressBars.linesRemaining', { count: lines.remaining });
  })();

  const footnote = (
    <FootnoteBlock accent={accent} lines={[null, needLine, null]} />
  );

  return (
    <SubCard
      kicker={t('progressBars.linesTitle')}
      icon={<Users size={11} strokeWidth={2.2} />}
      accent={accent}
      currentLabel={currentLabel}
      nextLabel={nextLabel}
      isMaxed={lines.isMaxed}
      footnote={footnote}
    >
      {!lines.isMaxed && lines.nextLineThreshold !== null && (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[11px] uppercase tracking-[0.14em]
                             text-[var(--text-secondary)] font-mono">
              {t('progressBars.linesBarLabel')}
            </span>
            <span
              className="text-[13px] font-display font-extrabold tabular-nums tracking-tight"
              style={{
                color: isComplete ? accent : 'var(--text-primary)',
              }}
            >
              {lines.activeCount}
              <span className="text-[var(--text-secondary)] mx-1 font-normal">/</span>
              <span style={{ color: 'var(--text-secondary)' }}>
                {lines.nextLineThreshold}
              </span>
            </span>
          </div>
          <div
            className="relative h-2 rounded-full overflow-hidden"
            style={{
              background:
                'color-mix(in oklab, var(--glass-stroke) 55%, transparent)',
              boxShadow: 'inset 0 1px 1px rgba(0,0,0,0.35)',
            }}
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full"
              style={{
                width: `${pct}%`,
                background: isComplete
                  ? `linear-gradient(90deg,
                      color-mix(in oklab, ${accent} 80%, transparent) 0%,
                      ${accent} 100%)`
                  : `linear-gradient(90deg,
                      color-mix(in oklab, ${accent} 45%, transparent) 0%,
                      color-mix(in oklab, ${accent} 85%, transparent) 100%)`,
                boxShadow: isComplete
                  ? `0 0 10px color-mix(in oklab, ${accent} 80%, transparent),
                     inset 0 1px 0 rgba(255,255,255,0.25)`
                  : `0 0 6px color-mix(in oklab, ${accent} 45%, transparent)`,
                transition: 'width 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </div>
        </div>
      )}
    </SubCard>
  );
}

function RankContent({
  rank,
  t,
}: {
  rank: RankProgress;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const accent = 'var(--accent-primary)'; // lunar / yellow
  const currentLabel = rank.currentLevel > 0 && rank.currentName
    ? `${rank.currentName} · R${rank.currentLevel}`
    : t('progressBars.noRankYet');

  const explainer = rank.isMaxed ? null : t('progressBars.rankExplainer');

  const needLine = (() => {
    if (rank.isMaxed) {
      if (rank.currentBoostPct) {
        return t('progressBars.maxRankHint', { boost: rank.currentBoostPct });
      }
      return t('progressBars.maxStatusHint');
    }
    if (!rank.l1Turnover30d) return null;
    const rem = parseFloat(rank.l1Turnover30d.remaining);
    if (rem <= 0) return t('progressBars.rankReady');
    return t('progressBars.rankRemaining', {
      amount: formatCompactUsdt(rem),
    });
  })();

  const rewardLine = (() => {
    if (rank.isMaxed) return null;
    const bonus = rank.nextBonusAmount
      ? formatUsdt(rank.nextBonusAmount)
      : null;

    // Кейс 1: у юзера ещё нет ранга → первый boost (например +0.3%)
    if (bonus && rank.nextBoostPct !== null && rank.currentBoostPct === null) {
      return t('progressBars.rankRewardFull', {
        amount: bonus,
        boost: rank.nextBoostPct,
      });
    }
    // Кейс 2: ранг уже есть, апгрейдится → boost меняется (+0.3% → +0.5%)
    if (bonus && rank.nextBoostPct !== null && rank.currentBoostPct !== null) {
      return t('progressBars.rankRewardFullBoostChange', {
        amount: bonus,
        from: rank.currentBoostPct,
        to: rank.nextBoostPct,
      });
    }
    // Fallback: только разовый бонус без boost
    if (bonus) {
      return t('progressBars.rankRewardOnlyBonus', { amount: bonus });
    }
    return null;
  })();

  const footnote = <FootnoteBlock accent={accent} lines={[explainer, needLine, rewardLine]} />;

  return (
    <SubCard
      kicker={t('progressBars.rankTitle')}
      icon={<Zap size={11} strokeWidth={2.2} />}
      accent={accent}
      currentLabel={currentLabel}
      nextLabel={rank.isMaxed ? null : rank.nextName}
      isMaxed={rank.isMaxed}
      footnote={footnote}
    >
      {rank.l1Turnover30d && (
        <BarRow
          label={t('progressBars.l1Label')}
          bar={rank.l1Turnover30d}
          accent={accent}
        />
      )}
    </SubCard>
  );
}

// ────────────────────────────────────────────────────────────────────
// Main: GlassSurface-wrapped collapsible panel
// ────────────────────────────────────────────────────────────────────

interface ProgressBarsProps {
  progress: ProgressSnapshot | null;
  loading: boolean;
  expanded: boolean;
  onToggle: () => void;
}

export function ProgressBars({
  progress, loading, expanded, onToggle,
}: ProgressBarsProps) {
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const uiSounds = useUISounds();

  const toggle = useCallback(() => {
    haptic.light();
    uiSounds.tap();
    onToggle();
  }, [haptic, uiSounds, onToggle]);

  // Мини-подпись в свёрнутом состоянии — компактный формат:
  // 🛡 = статус (0..20), 🏆 = ранг (0..5).
  // Иконки универсальны и тесно вписываются в kicker-строку.
  const collapsedSummary = (() => {
    if (!progress) return t('progressBars.loading');
    const s = progress.status;
    const r = progress.rank;
    const sPart = s.currentLevel > 0 && s.currentName
      ? `🛡 ${s.currentName} ${s.currentLevel}/20`
      : `🛡 0/20`;
    const rPart = r.currentLevel > 0 && r.currentName
      ? `🏆 ${r.currentName} ${r.currentLevel}/5`
      : `🏆 0/5`;
    return `${sPart} · ${rPart}`;
  })();

  return (
    <GlassSurface variant="strong" className="p-4 relative overflow-hidden">
      {/* Декоративный градиентный блоб слева — в стиле HERO */}
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          top: -80,
          left: -60,
          width: 220,
          height: 220,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, color-mix(in oklab, var(--accent-secondary) 30%, transparent) 0%, transparent 65%)',
          opacity: 0.55,
        }}
      />

      {/* Header — клик раскрывает / сворачивает */}
      <button
        type="button"
        onClick={toggle}
        className="relative w-full flex items-center justify-between gap-3 transition active:scale-[0.995]"
        aria-expanded={expanded}
        aria-label={expanded ? t('progressBars.collapseAria') : t('progressBars.expandAria')}
      >
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-[11px] uppercase tracking-[0.2em]
                        text-[var(--text-secondary)] font-mono">
            {t('progressBars.headerTitle')}
          </p>
          {!expanded && (
            <span className="text-[12px] text-[var(--text-secondary)] truncate">
              {collapsedSummary}
            </span>
          )}
        </div>
        <motion.div
          animate={{ rotate: expanded ? 0 : -90 }}
          transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
          className="text-[var(--text-muted)]"
        >
          <ChevronDown size={16} strokeWidth={2} />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="progress-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.26, ease: [0.32, 0.72, 0, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <div className="relative pt-3 flex flex-col gap-2.5">
              {loading && !progress ? (
                <div className="text-[11px] text-[var(--text-muted)] text-center py-4
                                font-mono">
                  {t('progressBars.loading')}
                </div>
              ) : progress ? (
                <>
                  <StatusContent status={progress.status} t={t} />
                  <RankContent rank={progress.rank} t={t} />
                  <LinesContent lines={progress.lines} t={t} />
                </>
              ) : (
                <div className="text-[11px] text-[var(--text-muted)] text-center py-4">
                  {t('progressBars.loadError')}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </GlassSurface>
  );
}
